import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {Machine} from '@models/machine/machine';
import {CommandRequest} from '@models/pages/user-defined-page/command';
import {ImageDetails} from '@models/pages/user-defined-page/image-widget/image-details';
import {ImageSourceType} from '@models/pages/user-defined-page/user-defined-chart-details';
import {WidgetIdList} from '@models/pages/user-defined-page/widget-id-list';
import {UserDefinedTabSettingsRequest} from '@models/sidebar/request/user-defined-tab-settings-request';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {BehaviorSubject, Observable, Subject, throwError} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserDefinedPageService {

  GET_WIDGET_DATA_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId/getWidgetData/:widgetId?timestamp=:timestamp&initial=:initial&sampling=:sampling&templateWidget=:templateWidget';
  private GET_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId/:pageId/getTab';
  private GET_PAGE_WIDGETS_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId/getTabWidgets';
  private SAVE_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId';
  private SEND_COMMAND_WRITEBACK_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId/postWriteBack/:gatewayId';
  private CLONE_USER_DEFINED_PAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/:userId/:pageId/:shared/:template/clone';
  private GET_IMAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/imageWidget/images/getImage?imageSourceType=:imageSourceType&bucketPath=:bucketPath&imageNameOrMetaKey=:imageNameOrMetaKey';
  private SAVE_IMAGE_URL_SUFFIX = '/api/web/base/:machineId/userMenu/userDefinedPage/imageWidget/uploadImage';
  private WRITEBACK_RESULT_URL = '/api/web/base/:machineId/userMenu/userDefinedPage/writeBackResult/:gatewayId/:correlationId';
  private readonly BASE_URL: string;

  saveSuccess$: Subject<boolean> = new BehaviorSubject(false);
  onEditLayout$: Subject<boolean> = new Subject();

  constructor(private http: HttpClient, private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public getPage(machineId: string, firebaseUserId: string, pageId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':userId', firebaseUserId)
    .replace(':pageId', String(pageId));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getPageWidgets(machineId: string, firebaseUserId: string, widgetIdList: WidgetIdList): Observable<any> {
    let url: string = this.BASE_URL + this.GET_PAGE_WIDGETS_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':userId', firebaseUserId);
    return this.http.post(url, widgetIdList, HTTPS_REQUEST_OPTIONS);
  }

  public savePage(firebaseUserId: string, machineId: string, request: UserDefinedTabSettingsRequest): Observable<any> {
    let url: string = this.BASE_URL + this.SAVE_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':userId', firebaseUserId);
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public getImage(machine: Machine, imageSourceType: ImageSourceType, bucketPath: string, imageNameOrMetaKey): Observable<any> {
    if (!machine || !machine.id) {
      return throwError(() => new Error('invalid request parameters'));
    }

    let url: string = this.BASE_URL + this.GET_IMAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machine.id))
    .replace(':imageSourceType', imageSourceType)
    .replace(':bucketPath', encodeURIComponent(bucketPath))
    .replace(':imageNameOrMetaKey', encodeURIComponent(imageNameOrMetaKey));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public saveImage(machine: Machine, imageWidgetBucketName: string, uploadedFileName: string, imageContent: string): Observable<any> {
    if (!machine || !machine.id) {
      return throwError(() => new Error('invalid request parameters'));
    }
    const bucketPath = imageWidgetBucketName + '/';
    const imageDetails = new ImageDetails(
        bucketPath,
        ImageSourceType.IMAGE_UPLOAD,
        uploadedFileName,
        imageContent
        .replace(/data:(.+);base64,/i, '')
    );

    let url: string = this.BASE_URL + this.SAVE_IMAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machine.id));
    return this.http.post(url, imageDetails, HTTPS_REQUEST_OPTIONS);
  }

  public clonePage(sourcePageId: string, firebaseUserId: string, sourceMachineId: string, targetMachineIds: number[], sharedPage: boolean, isTemplatePage: boolean): Observable<any> {

    let url: string = this.BASE_URL + this.CLONE_USER_DEFINED_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(sourceMachineId))
    .replace(':userId', firebaseUserId)
    .replace(':pageId', String(sourcePageId))
    .replace(':shared', String(sharedPage))
    .replace(':template', String(isTemplatePage));

    return this.http.post(url, targetMachineIds, HTTPS_REQUEST_OPTIONS);
  }

  public sendWritebackCommand(machineId: string, userId: string, gatewayId: string, request: CommandRequest): Observable<any> {
    let url: string = this.BASE_URL + this.SEND_COMMAND_WRITEBACK_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':userId', userId)
    .replace(':gatewayId', gatewayId);

    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public writebackResult(machineId: string, gatewayId: string, correlationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.WRITEBACK_RESULT_URL;
    url = url.replace(':machineId', machineId)
    .replace(':correlationId', String(correlationId))
    .replace(':gatewayId', gatewayId);

    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }
}
